import { getPeriodName } from "./getPeriodName.ts";
import { choice } from "../../../../common/random.ts";
import { helpers } from "./helpers.ts";
import { local } from "./local.ts";
import type { PlayByPlayEventOutput } from "../../../../common/types/gameSim.ts";
import type { ReactNode } from "react";
import { formatClock } from "../../../../common/formatClock.ts";
import { formatLiveGameStat } from "./formatLiveGameStat.ts";
import type { PlayByPlayEvent } from "../../../../common/types/gameSim.ts";
import i18n from "./i18n.ts";
import { ordinalLocalized } from "./ordinalLocalized.ts";

const getPronoun = (pronoun: Parameters<typeof helpers.pronoun>[1]) => {
	return helpers.pronoun(local.getState().gender, pronoun);
};

// Translate a pronoun string (He/She) through i18n for the current language.
const tPronoun = (p: string): string =>
	i18n.t(`pronoun.${p}`, { ns: "ui", defaultValue: p });

// Shorthand for play-by-play translations.
const t = (key: string, vars?: Record<string, string | number>): string =>
	i18n.t(`pbp.${key}`, { ns: "ui", ...(vars ?? {}) });

let playersByPidGid: number | undefined;
let playersByPid:
	| Record<
			number,
			{
				[key: string]: number | string | boolean; // bit of a hack, generic key names which can be either a number, string or boolean. essentially an any type
				name: string;
				inGame: boolean;
			}
	  >
	| undefined;

const getName = (pid: number) => {
	return playersByPid?.[pid]?.name ?? "???";
};

export const getText = (
	event: PlayByPlayEventOutput,
	boxScore: {
		numPeriods: number;
		teams: [{ pts: number; players: any[] }, { pts: number; players: any[] }];
	},
) => {
	let texts: ReactNode[] | undefined;
	let weights;
	if (playersByPid === undefined) {
		throw new Error("playersByPid is undefined");
	}

	if (event.type === "period") {
		texts = [
			t("period", { n: event.period, periodName: getPeriodName(boxScore.numPeriods) }),
		];
	} else if (event.type === "overtime") {
		const overtimes = event.period - boxScore.numPeriods;
		texts = [
			overtimes === 1
				? t("overtime")
				: t("overtimeN", { n: overtimes }),
		];
	} else if (event.type === "injury") {
		texts = [t("injury", { name: getName(event.pid) })];
	} else if (event.type === "tov") {
		if (event.outOfBounds) {
			texts = [
				`${t("tov_outOfBounds", { name: getName(event.pid) })}${formatLiveGameStat(playersByPid[event.pid], "tov")}`,
			];
		} else {
			texts = [
				`${t("tov", { name: getName(event.pid) })}${formatLiveGameStat(playersByPid[event.pid], "tov")}`,
			];
		}
	} else if (event.type === "stl") {
		if (event.outOfBounds) {
			texts = [
				`${t("stl_outOfBounds", { name: getName(event.pid), name2: getName(event.pidTov) })}${formatLiveGameStat(playersByPid[event.pid], "stl")}${formatLiveGameStat(playersByPid[event.pidTov], "tov")}`,
			];
		} else {
			texts = [
				`${t("stl", { name: getName(event.pid), name2: getName(event.pidTov) })}${formatLiveGameStat(playersByPid[event.pid], "stl")}${formatLiveGameStat(playersByPid[event.pidTov], "tov")}`,
			];
		}
	} else if (event.type === "fgaTipIn") {
		texts = [
			t("fgaTipIn", { name: getName(event.pid), name2: getName(event.pidPass) }),
		];
	} else if (event.type === "fgaPutBack") {
		texts = [t("fgaPutBack", { name: getName(event.pid) })];
	} else if (event.type === "fgaAtRim") {
		texts = [t("fgaAtRim", { name: getName(event.pid) })];
	} else if (event.type === "fgaLowPost") {
		texts = [t("fgaLowPost", { name: getName(event.pid) })];
	} else if (event.type === "fgaMidRange") {
		texts = [t("fgaMidRange", { name: getName(event.pid) })];
	} else if (event.type === "fgaTp") {
		if (event.desperation) {
			texts = [t("fgaTp_desperation", { name: getName(event.pid) })];
		} else {
			texts = [t("fgaTp", { name: getName(event.pid) })];
		}
	} else if (event.type === "fgaTpFake") {
		// This is for when threePointers is false
		if (event.desperation) {
			texts = [t("fgaTpFake_desperation", { name: getName(event.pid) })];
		} else {
			texts = [t("fgaTpFake", { name: getName(event.pid) })];
		}
	} else if (event.type === "fgTipIn") {
		const he = tPronoun(getPronoun("He"));
		texts = [
			`${t("fgTipIn_slam", { pronoun: he })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
			`${t("fgTipIn_tip", { pronoun: he })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
		];
		weights = local.getState().gender === "male" ? [1, 1] : [0, 1];
	} else if (event.type === "fgTipInAndOne") {
		const he = tPronoun(getPronoun("He"));
		texts = [
			`${t("fgTipInAndOne_slam", { pronoun: he })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
			`${t("fgTipInAndOne_tip", { pronoun: he })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
		];
		weights = local.getState().gender === "male" ? [1, 1] : [0, 1];
	} else if (event.type === "fgPutBack") {
		const he = tPronoun(getPronoun("He"));
		texts = [
			`${t("fgPutBack_slam", { pronoun: he })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
			`${t("fgPutBack_lay", { pronoun: he })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
		];
		weights = local.getState().gender === "male" ? [1, 1] : [0, 1];
	} else if (event.type === "fgPutBackAndOne") {
		const he = tPronoun(getPronoun("He"));
		texts = [
			`${t("fgPutBackAndOne_slam", { pronoun: he })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
			`${t("fgPutBackAndOne_lay", { pronoun: he })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
		];
		weights = local.getState().gender === "male" ? [1, 1] : [0, 1];
	} else if (event.type === "fgAtRim") {
		const he = tPronoun(getPronoun("He"));
		texts = [
			`${t("fgAtRim_dunk", { pronoun: he, name: getName(event.pidDefense) })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
			`${t("fgAtRim_slam", { pronoun: he })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
			`${t("fgAtRim_layup")}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
		];
		weights = local.getState().gender === "male" ? [1, 2, 2] : [1, 10, 1000];
	} else if (event.type === "fgAtRimAndOne") {
		const he = tPronoun(getPronoun("He"));
		texts = [
			`${t("fgAtRimAndOne_dunk", { pronoun: he, name: getName(event.pidDefense) })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
			`${t("fgAtRimAndOne_slam", { pronoun: he })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
			`${t("fgAtRimAndOne_layup")}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
		];
		weights = local.getState().gender === "male" ? [1, 2, 2] : [1, 10, 1000];
	} else if (
		event.type === "fgLowPost" ||
		event.type === "fgMidRange" ||
		event.type === "tp"
	) {
		texts = [`${t("fgGood")}${formatLiveGameStat(playersByPid[event.pid], "pts")}`];
	} else if (
		event.type === "fgLowPostAndOne" ||
		event.type === "fgMidRangeAndOne" ||
		event.type === "tpAndOne"
	) {
		texts = [
			`${t("fgGoodAndOne")}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
		];
	} else if (
		event.type === "blkAtRim" ||
		event.type === "blkTipIn" ||
		event.type === "blkPutBack"
	) {
		texts = [
			`${t("blkAtRim_layup", { name: getName(event.pid) })}${formatLiveGameStat(playersByPid[event.pid], "blk")}`,
			`${t("blkAtRim_dunk", { name: getName(event.pid) })}${formatLiveGameStat(playersByPid[event.pid], "blk")}`,
		];
		if (local.getState().gender === "female") {
			weights = [1, 0];
		}
	} else if (
		event.type === "blkLowPost" ||
		event.type === "blkMidRange" ||
		event.type === "blkTp"
	) {
		texts = [
			`${t("blkPost", { name: getName(event.pid) })}${formatLiveGameStat(playersByPid[event.pid], "blk")}`,
		];
	} else if (event.type === "missTipIn") {
		const he = tPronoun(getPronoun("He"));
		texts = [
			t("missTipIn_layup", { pronoun: he }),
			t("missTipIn_dunk", { pronoun: he }),
			t("missTipIn_no"),
		];
		if (local.getState().gender === "female") {
			weights = [1, 0, 1];
		}
	} else if (event.type === "missAtRim" || event.type === "missPutBack") {
		const he = tPronoun(getPronoun("He"));
		texts = [
			t("missAtRim_he", { pronoun: he }),
			t("missAtRim_roll"),
			t("missAtRim_no"),
		];
		weights = [1, 1, 3];
	} else if (
		event.type === "missLowPost" ||
		event.type === "missMidRange" ||
		event.type === "missTp"
	) {
		const he = tPronoun(getPronoun("He"));
		texts = [t("missShot_rim"), t("missShot_no"), t("missShot_brick", { pronoun: he })];
		weights = [1, 4, 1];
	} else if (event.type === "orb") {
		const trb =
			(playersByPid[event.pid] as any).drb +
			(playersByPid[event.pid] as any).orb;
		texts = [
			`${t("orb", { name: getName(event.pid) })}${formatLiveGameStat(trb, "trb")}`,
		];
	} else if (event.type === "drb") {
		const trb =
			(playersByPid[event.pid] as any).drb +
			(playersByPid[event.pid] as any).orb;
		texts = [
			`${t("drb", { name: getName(event.pid) })}${formatLiveGameStat(trb, "trb")}`,
		];
	} else if (event.type === "gameOver") {
		texts = [t("gameOver")];
	} else if (event.type === "ft") {
		texts = [
			`${t("ft", { name: getName(event.pid) })}${formatLiveGameStat(playersByPid[event.pid], "pts")}`,
		];
	} else if (event.type === "missFt") {
		texts = [t("missFt", { name: getName(event.pid) })];
	} else if (event.type === "pfNonShooting") {
		texts = [
			`${t("pfNonShooting", { name: getName(event.pid) })}${formatLiveGameStat(playersByPid[event.pid], "pf")}`,
		];
	} else if (event.type === "pfBonus") {
		texts = [
			<>
				{t("pfBonusLine1", { name: getName(event.pid) })}
				{formatLiveGameStat(playersByPid[event.pid], "pf")}
				<br />
				{t("pfBonusLine2", { name2: getName(event.pidShooting) })}
			</>,
		];
	} else if (event.type === "pfFG") {
		texts = [
			<>
				{t("pfFGLine1", { name: getName(event.pid) })}
				{formatLiveGameStat(playersByPid[event.pid], "pf")}
				<br />
				{t("pfFGLine2", { name2: getName(event.pidShooting) })}
			</>,
		];
	} else if (event.type === "pfTP") {
		texts = [
			<>
				{t("pfTPLine1", { name: getName(event.pid) })}
				{formatLiveGameStat(playersByPid[event.pid], "pf")}
				<br />
				{t("pfTPLine2", { name2: getName(event.pidShooting) })}
			</>,
		];
	} else if (event.type === "foulOut") {
		texts = [
			<span className="text-danger">{t("foulOut", { name: getName(event.pid) })}</span>,
		];
	} else if (event.type === "sub") {
		texts = [
			<>
				{t("subOn")} {event.pids.map((pid) => getName(pid)).join(", ")}
				<br />
				{t("subOff")} {event.pidsOff.map((pid) => getName(pid)).join(", ")}
			</>,
		];
	} else if (event.type === "jumpBall") {
		texts = [
			t("jumpBall", { name: getName(event.pid), name2: getName(event.pid2) }),
		];
	} else if (event.type === "elamActive") {
		texts = [t("elamActive", { n: event.target })];
	} else if (event.type === "timeout") {
		texts = [
			event.advancesBall
				? t("timeoutAdvance", { n: event.numLeft })
				: t("timeout", { n: event.numLeft }),
		];
	} else if (event.type === "endOfPeriod") {
		if (event.reason === "runOutClock") {
			texts = [t("endOfPeriod_clock")];
		} else if (event.reason === "noShot") {
			texts = [t("endOfPeriod_noShot")];
		} else {
			texts = [t("endOfPeriod_defense")];
		}
	} else if (event.type === "outOfBounds") {
		texts = [t("outOfBounds", { side: event.on })];
	} else if (event.type === "shootoutStart") {
		texts = [t("shootoutStart", { n: event.rounds })];
	} else if (event.type === "shootoutTeam") {
		texts = [t("shootoutTeam", { name: getName(event.pid) })];
	} else if (event.type === "shootoutShot") {
		const he = tPronoun(getPronoun("He"));
		texts = event.made
			? [t("shootoutShot_good_1"), t("shootoutShot_good_2"), t("shootoutShot_good_3")]
			: [
					t("shootoutShot_miss_1"),
					t("shootoutShot_miss_2", { pronoun: he }),
					t("shootoutShot_miss_3", { pronoun: he }),
				];
		weights = event.made ? [1, 0.25, 0.25] : [1, 0.1, 0.01];
	} else if (event.type === "shootoutTie") {
		texts = [t("shootoutTie")];
	}

	if (texts) {
		let text = choice(texts, weights);

		const eAny = event as any;
		if (eAny.pidAst !== undefined) {
			text = (
				<>
					{text}
					<br />
					<span className="text-body-secondary">
						{t("assistedBy", { name: getName(eAny.pidAst) })}
						{formatLiveGameStat(playersByPid[eAny.pidAst], "ast")}
					</span>
				</>
			);
		}

		if (eAny.pidFoul !== undefined) {
			// Hack! need to add 1 because the pf stat is not logged until after this event, and it's tricky to fix that
			const pf = (playersByPid[eAny.pidFoul]!.pf as number) + 1;
			text = (
				<>
					{text}
					<br />
					<span className="text-body-secondary">
						{t("foulOn", { name: getName(eAny.pidFoul) })}
						{formatLiveGameStat(pf, "pf")}
					</span>
				</>
			);
		}

		return text;
	} else {
		throw new Error(`No text for ${event.type}`);
	}
};

// false means assign possession to other team
const newPossessionTypes: Record<string, boolean> = {
	fgAtRim: true,
	fgAtRimAndOne: true,
	fgLowPost: true,
	fgLowPostAndOne: true,
	fgMidRange: true,
	fgMidRangeAndOne: true,
	ft: true,
	tp: true,
	tpAndOne: true,
	drb: true,
	fgaTipIn: true,
	fgaPutBack: true,
	fgaAtRim: true,
	fgaLowPost: true,
	fgaMidRange: true,
	fgaTp: true,
	fgaTpFake: true,
	jumpBall: true,
	missAtRim: true,
	missFt: true,
	missLowPost: true,
	missMidRange: true,
	missTp: true,
	orb: true,
	stl: true,
	tov: false,
	timeout: true,
	endOfPeriod: true,
};

// Mutates boxScore!!!
const processLiveGameEvents = ({
	events,
	boxScore,
	overtimes,
	quarters,
}: {
	events: PlayByPlayEvent<PlayByPlayEventOutput>[];
	boxScore: any;
	overtimes: number;
	quarters: string[];
}) => {
	if (
		!playersByPid ||
		boxScore.gid !== playersByPidGid ||
		events[0]?.type === "init"
	) {
		playersByPidGid = boxScore.gid;
		playersByPid = {};
		for (const t of boxScore.teams) {
			for (const p of t.players) {
				playersByPid[p.pid] = p;
			}
		}
	}
	let stop = false;
	let text;
	let t: 0 | 1 | undefined;
	let textOnly = false;
	let eventType: string | undefined;

	while (!stop && events.length > 0) {
		const e = events.shift();
		if (!e) {
			continue;
		}

		const eAny = e as any;

		// Swap teams order, so home team is at bottom in box score
		const actualT = eAny.t === 0 ? 1 : eAny.t === 1 ? 0 : undefined;

		// Hacky quarter stuff, ugh
		if (
			e.type === "period" ||
			e.type === "overtime" ||
			boxScore.quarter === ""
		) {
			boxScore.teams[0].ptsQtrs.push(0);
			boxScore.teams[1].ptsQtrs.push(0);
			const quarter = boxScore.teams[0].ptsQtrs.length;
			if (quarter > boxScore.numPeriods) {
				overtimes = quarter - boxScore.numPeriods;
				if (overtimes === 1) {
					boxScore.overtime = "(OT)";
				} else if (overtimes > 1) {
					boxScore.overtime = `(${overtimes}OT)`;
				}
				boxScore.quarter = i18n.t("liveGame.overtimeOrdinal", {
					ns: "ui",
					defaultValue: "{{ordinal}} overtime",
					ordinal: ordinalLocalized(overtimes),
				});
				boxScore.quarterShort = overtimes === 1 ? "OT" : `${overtimes}OT`;
			} else {
				boxScore.quarter = i18n.t("liveGame.periodOrdinal", {
					ns: "ui",
					defaultValue: "{{ordinal}} {{period}}",
					ordinal: ordinalLocalized(quarter),
					period: getPeriodName(boxScore.numPeriods),
				});
				boxScore.quarterShort = `${getPeriodName(
					boxScore.numPeriods,
					true,
				)}${quarter}`;
				quarters.push(boxScore.quarterShort);
			}
		} else if (e.type === "shootoutStart") {
			boxScore.shootout = true;
			boxScore.teams[0].sPts = 0;
			boxScore.teams[0].sAtt = 0;
			boxScore.teams[1].sPts = 0;
			boxScore.teams[1].sAtt = 0;
		}

		if (e.type === "stat") {
			// Quarter-by-quarter score
			if (e.s === "pts") {
				const ptsQtrs = boxScore.teams[actualT!].ptsQtrs;
				ptsQtrs[ptsQtrs.length - 1] += e.amt;
				boxScore.teams[actualT!].ptsQtrs = ptsQtrs;
			}

			// Everything else
			if (
				e.s === "min" ||
				e.s === "fg" ||
				e.s === "fga" ||
				e.s === "tp" ||
				e.s === "tpa" ||
				e.s === "ft" ||
				e.s === "fta" ||
				e.s === "orb" ||
				e.s === "drb" ||
				e.s === "ast" ||
				e.s === "tov" ||
				e.s === "stl" ||
				e.s === "blk" ||
				e.s === "ba" ||
				e.s === "pf" ||
				e.s === "pts"
			) {
				const p = playersByPid[e.pid!];
				(p as any)[e.s] += e.amt;
				boxScore.teams[actualT!][e.s] += e.amt;

				if (e.s === "pts") {
					for (let j = 0; j < 2; j++) {
						for (let k = 0; k < boxScore.teams[j].players.length; k++) {
							if (boxScore.teams[j].players[k].inGame) {
								boxScore.teams[j].players[k].pm +=
									actualT === j ? e.amt : -e.amt;
							}
						}
					}
				}
			} else if (e.s === "gs") {
				const p = playersByPid[e.pid!]!;
				p.inGame = true;
			} else if (e.s === "sPts" || e.s === "sAtt") {
				// Shootout
				boxScore.teams[actualT!][e.s] += e.amt;
			}
		} else if (e.type === "timeouts") {
			// Reversed for actualT
			boxScore.teams[0].timeouts = e.timeouts[1];
			boxScore.teams[1].timeouts = e.timeouts[0];
		} else if (e.type !== "init") {
			text = getText(e, boxScore);
			t = actualT;
			eventType = e.type;
			textOnly =
				e.type === "gameOver" ||
				e.type === "period" ||
				e.type === "overtime" ||
				e.type === "elamActive" ||
				e.type === "shootoutStart" ||
				e.type === "shootoutTie" ||
				e.type === "shootoutTeam";

			let time;
			if (eAny.clock !== undefined) {
				const seconds = eAny.clock;
				time = formatClock(seconds);
			}

			if (e.type === "injury") {
				const p = playersByPid[e.pid];
				if (p) {
					(p as any).injury = {
						type: "Injured",
						gamesRemaining: -1,
					};
				}
			}

			if (e.type === "sub") {
				for (const pid of e.pids) {
					playersByPid[pid]!.inGame = true;
				}
				for (const pid of e.pidsOff) {
					playersByPid[pid]!.inGame = false;
				}
			} else if (e.type === "elamActive") {
				boxScore.elamTarget = e.target;
			}

			if (time) {
				boxScore.time = time;
			}

			if (Object.hasOwn(newPossessionTypes, eAny.type)) {
				boxScore.possession = newPossessionTypes[eAny.type]
					? actualT
					: actualT === 0
						? 1
						: 0;
			}

			stop = true;
		}
	}

	return {
		// C3: the stop event's raw type, so the mobile gamecast can weight key
		// plays (threes / dunk family / blocks / clutch detection in the view).
		eventType,
		overtimes,
		quarters,
		t,
		text,
		textOnly,
	};
};

export default processLiveGameEvents;
